import { useState } from 'react';
import { useCurrency } from '@/contexts/CurrencyContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Check, ChevronDown, Loader2 } from 'lucide-react';

interface CurrencySelectorProps {
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  showLabel?: boolean;
}

export function CurrencySelector({ 
  variant = 'outline', 
  size = 'sm',
  showLabel = true 
}: CurrencySelectorProps) {
  const { currency, currencies, isLoading, setCurrency } = useCurrency();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSelect = async (newCurrency: typeof currency) => {
    if (!newCurrency || newCurrency.code === currency?.code) return;
    
    setIsUpdating(true);
    try {
      await setCurrency(newCurrency);
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <Button variant={variant} size={size} disabled>
        <Loader2 className="w-4 h-4 animate-spin" />
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} disabled={isUpdating}>
          <span className="mr-2">{currency?.flag}</span>
          {showLabel && <span>{currency?.code}</span>}
          {isUpdating ? (
            <Loader2 className="w-4 h-4 ml-2 animate-spin" />
          ) : (
            <ChevronDown className="w-4 h-4 ml-2" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
        {currencies.map((c) => (
          <DropdownMenuItem
            key={c.code}
            onClick={() => handleSelect(c)}
            className="flex items-center justify-between"
          >
            <div className="flex items-center">
              <span className="mr-2 text-lg">{c.flag}</span>
              <div>
                <div className="font-medium">{c.code}</div>
                <div className="text-xs text-gray-500">{c.name}</div>
              </div>
            </div>
            {currency?.code === c.code && (
              <Check className="w-4 h-4 text-green-600 ml-4" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default CurrencySelector;
